import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule, Routes } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NgxSpinnerModule } from "ngx-spinner";
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from "@angular/material/core";
import { MY_DATE_FORMATS } from "app/shared/utils/custom-date-format";
import {
    MAT_MOMENT_DATE_ADAPTER_OPTIONS,
    MomentDateAdapter,
} from "@angular/material-moment-adapter";
import { FuseModule } from "@fuse/fuse.module";
import { FuseSharedModule } from "@fuse/shared.module";
import {
    FuseProgressBarModule,
    FuseSidebarModule,
    FuseThemeOptionsModule,
} from "@fuse/components";
import { LoginComponent } from "app/main/sessions/login/login.component";
import { ForgotPasswordComponent } from "app/main/sessions/forgot-password/forgot-password.component";
import { RegisterComponent } from "app/main/sessions/register/register.component";
import { fuseConfig } from "app/fuse-config";
import { HomeComponent } from "app/main/home/home.component";
import { AppComponent } from "app/app.component";
import { LayoutModule } from "app/layout/layout.module";
import { DefaultInterceptor } from "app/shared/interceptors/default.interceptor";
import { environment } from "environments/environment";
import { SharedModule } from "app/shared/shared.module";
import { AgmCoreModule } from "@agm/core";
import { CdkColumnDef } from "@angular/cdk/table";
import {
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
} from "@angular-material-components/datetime-picker";
import { NgApexchartsModule } from "ng-apexcharts";
import { AuthGuard } from "./shared/interceptors/auth-gaurd";
import { BookingModule } from "app/main/booking/booking.module";
import { WebLoginComponent } from "./main/web-payment/web-login/web-login.component";
import { ScanChargerComponent } from './main/web-payment/scan-charger/scan-charger.component';
import { ChargerDetailComponent } from './main/web-payment/charger-detail/charger-detail.component';
import { ChargerStatusComponent } from "./main/web-payment/charger-status/charger-status.component";
import { ChargerHomeComponent } from './main/web-payment/charger-home/charger-home.component';
import { ChargerSummaryComponent } from './main/web-payment/charger-summary/charger-summary.component';
import { ChargeHistoryComponent } from './main/web-payment/charge-history/charge-history.component';
const COMPONENTS = [
    LoginComponent,
    ForgotPasswordComponent,
    RegisterComponent,
    HomeComponent,
    LoginComponent, 
    WebLoginComponent, 
    ScanChargerComponent, 
    ChargerDetailComponent, 
    ChargerStatusComponent, 
    ChargerHomeComponent, 
    ChargerSummaryComponent,
    ChargeHistoryComponent
];

const appRoutes: Routes = [
    // #############################################################################################
    {
        path: "login",
        component: LoginComponent,
        data: { title: "Login", titleI18n: "login" },
    },
    
    {
        path: "register",
        component: RegisterComponent,
        data: { title: "Register", titleI18n: "register" },
    },

    {
        path: "",
        canActivate: [AuthGuard],
        children: [
            { path: "", redirectTo: "home", pathMatch: "full" },
            {
                path: "home",
                component: HomeComponent,
                data: { title: "Home", titleI18n: "Home" },
            },
            {
                path: "home/:id",
                component: HomeComponent,
                data: { title: "Home", titleI18n: "Home" },
            },
            {
                path: "profile",
                loadChildren: () =>
                    import("app/main/profile/profile.module").then(
                        (m) => m.ProfileModule
                    ),
                data: { title: "Profile", titleI18n: "profile" },
            },
            {
                path: "userManagement",
                loadChildren: () =>
                    import(
                        "app/main/user-management/user-management.module"
                    ).then((m) => m.UserManagementModule),
                data: { title: "User Management", titleI18n: "user" },
            },
            {
                path: "dashboardManagement",
                loadChildren: () =>
                    import("app/main/dashboard/dashboard.module").then(
                        (m) => m.DashboardModule
                    ),
            },
            {
                path: "clientManagement",
                loadChildren: () =>
                    import("app/main/client/client.module").then(
                        (m) => m.ClientModule
                    ),
                data: { title: "Client Management", titleI18n: "client" },
            },
            {
                path: "assetManagement",
                loadChildren: () =>
                    import("app/main/asset/asset.module").then(
                        (m) => m.AssetModule
                    ),
                data: { title: "Asset Management", titleI18n: "client" },
            },
            {
                path: "driverManagement",
                loadChildren: () =>
                    import("app/main/driver/driver.module").then(
                        (m) => m.DriverModule
                    ),
                data: { title: "Driver Management", titleI18n: "driver" },
            },
            {
                path: "cpoManagement",
                loadChildren: () =>
                    import("app/main/cpo/cpo.module").then((m) => m.CpoModule),
                data: { title: "CPO Management", titleI18n: "cpo" },
            },
            {
                path: "chargingStation",
                loadChildren: () =>
                    import(
                        "app/main/charger-station/charging-station.module"
                    ).then((m) => m.ChargingStationModule),
                data: {
                    title: "Charging Station",
                    titleI18n: "chargingStation",
                },
            },
            {
                path: "logsandHistory",
                loadChildren: () =>
                    import("app/main/logs-history/logs-history.module").then(
                        (m) => m.LogsHistoryModule
                    ),
                data: { title: "Logs and History", titleI18n: "client" },
            },
            {
                path: "forgotpassword",
                component: ForgotPasswordComponent,
                data: { title: "ForgotPassword", titleI18n: "forgotpassword" },
            },
            {
                path: "registration",
                loadChildren: () =>
                    import(
                        "app/main/registrationModule/registrationModule.module"
                    ).then((m) => m.RegistrationModuleModule),
                data: { title: "Registration", titleI18n: "registration" },
            },
            {
                path: "coprateUserManagement",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import("app/main/coprate-mgmt/coprate-mgmt.module").then(
                        (m) => m.CoprateMgmtModule
                    ),
                data: {
                    title: "Coprate User Management",
                    titleI18n: "coprateUser",
                },
            },
            {
                path: "manageGeographics",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import(
                        "app/main/manage-geographics/manage-geographics.module"
                    ).then((m) => m.ManageGeographicsModule),
                data: {
                    title: "Coprate User Management",
                    titleI18n: "coprateUser",
                },
            },
            {
                path: "mangeCallback",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import(
                        "app/main/callback-management/callback-management.module"
                    ).then((m) => m.CallbackManagementModule),
                data: { title: "Manage Callback", titleI18n: "manageCallback" },
            },
            {
                path: "information",
                loadChildren: () =>
                    import("app/main/information/information.module").then(
                        (m) => m.InformationModule
                    ),
                data: { title: "AboutUs", titleI18n: "AboutUs" },
            },
            {
                path: "bookingManagement",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import("app/main/booking/booking.module").then(
                        (m) => m.BookingModule
                    ),
                data: {
                    title: "Booking Management",
                    titleI18n: "BookingManagement",
                },
            },
            {
                path: "paymentManagement",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import("app/main/payment/payment.module").then(
                        (m) => m.PaymentModule
                    ),
                data: { title: "payment", titleI18n: "Payment" },
            },
            {
                path: "endUserManagement",
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import("app/main/endUser/end-user.module").then(
                        (m) => m.EndUserModule
                    ),
                data: { title: "EndUser", titleI18n: "EndUser" },
            },
            {
                path: "errorManagement",
                loadChildren: () =>
                    import("app/main/error/error.module").then(
                        (m) => m.ErrorModule
                    ),
                data: { title: "Error Module", titleI18n: "errorModule" },
            },
            {
                path: "menuManagement",
                loadChildren: () =>
                    import("app/main/menu-master/menu-master.module").then(
                        (m) => m.MenuMasterModule
                    ),
                data: { title: "Menu Management", titleI18n: "menuManagement" },
            },
            {
                path: "miscellaneous",
                loadChildren: () =>
                    import("app/main/miscellaneous/miscellaneous.module").then(
                        (m) => m.MiscellaneousModule
                    ),
                data: { title: "Miscellaneous", titleI18n: "miscellaneous" },
            },
            {
                path: "evSpace",
                loadChildren: () =>
                    import("app/main/EV-space/EV-space.module").then(
                        (m) => m.EVSpaceModule
                    ),
                data: { title: "EV Space", titleI18n: "EV-space" },
            },
            {
                path: "customerManagement",
                loadChildren: () =>
                    import(
                        "app/main/customer-management/customer-management.module"
                    ).then((m) => m.CustomerManagementModule),
                data: {
                    title: "Customer Management",
                    titleI18n: "Customer-Management",
                },
            },
            {
                path: "ocppChargerProfile",
                loadChildren: () =>
                    import(
                        "app/main/ocpp-charger-profile/ocpp-charger-profile.module"
                    ).then((m) => m.OccpChargerProfileModule),
                data: {
                    title: "ocppChargerProfile",
                    titleI18n: "Ocpp Charger Profile",
                },
            },
            {
                path: "dispatchManagement",
                loadChildren: () =>
                    import(
                        "app/main/dispatch-management/dispatch-management.module"
                    ).then((m) => m.DispatchManagementModule),
                data: {
                    title: "Dispatch Management",
                    titleI18n: "Dispatch Management",
                },
            },
            {
                path: "tariffManagement",
                loadChildren: () =>
                    import(
                        "app/main/tariff-management/tariff-management.module"
                    ).then((m) => m.TariffManagementModule),
                data: {
                    title: "Traiff Management",
                    titleI18n: "Tariff Management",
                },
            },
        ],
    },

    {
        path: "web-login",
        component: WebLoginComponent,
        data: { title: "Web Login", titleI18n: "Web Login" },
    },
    {
        path: "charger-home",
        component: ChargerHomeComponent,
        data: { title: "Charger Home", titleI18n: "Charger Home" },
    },
    {
        path: "scan-charger",
        component: ScanChargerComponent,
        data: { title: "Scan Charger", titleI18n: "Scan Charger" },
    },
    {
        path: "charger-detail",
        component: ChargerDetailComponent,
        data: { title: "Charger Detail", titleI18n: "Charger Detail" },
    },
    {
        path: "charger-status",
        component: ChargerStatusComponent,
        data: { title: "Charger Status", titleI18n: "Charger Status" },
    },
    {
        path: "charging-summary",
        component: ChargerSummaryComponent,
        data: { title: "Charging Summary", titleI18n: "Charging Summary" },
    },
    {
        path: "charging-history",
        component: ChargeHistoryComponent,
        data: { title: "Charging History", titleI18n: "Charging History" },
    },

    { path: "**", redirectTo: "home", pathMatch: "full" }, // Wildcard route for handling unknown paths or 404
];

@NgModule({
    declarations: [AppComponent, COMPONENTS],
    imports: [
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes, {
            useHash: environment.useHash,
            relativeLinkResolution: "legacy",
        }),

        TranslateModule.forRoot(),
        SharedModule,
        NgxMatDatetimePickerModule,
        NgxMatTimepickerModule,
        NgApexchartsModule,
        // Material moment date module
        // AgmCoreModule.forRoot({
        //     // "AIzaSyBcfmwbnIFIaPZlwZiFWAVq7Rk33OP_dL4",
        //     //  apiKey: "AIzaSyA6MzAQocq5JW4YFAXerO1NQfeaZIc-0TY",
        // }),
        AgmCoreModule.forRoot({
            apiKey: "AIzaSyA6MzAQocq5JW4YFAXerO1NQfeaZIc-0TY",
        }),
        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,

        // App modules
        LayoutModule,

        NgxSpinnerModule
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DefaultInterceptor,
            multi: true,
        },
        AuthGuard,
        CdkColumnDef,
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },

        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
